import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';

export default function Tenants() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', national_id: '',
    nationality: '', emergency_contact_name: '', emergency_contact_phone: ''
  });

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const res = await api.getTenants();
      setTenants(res.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) { await api.updateTenant(editingTenant.id, formData); }
      else { await api.createTenant(formData); }
      setDialogOpen(false);
      resetForm();
      fetchTenants();
    } catch (error) { console.error('Failed to save:', error); }
  };

  const resetForm = () => {
    setFormData({ full_name: '', email: '', phone: '', national_id: '', nationality: '', emergency_contact_name: '', emergency_contact_phone: '' });
    setEditingTenant(null);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      full_name: tenant.full_name, email: tenant.email, phone: tenant.phone,
      national_id: tenant.national_id || '', nationality: tenant.nationality || '',
      emergency_contact_name: tenant.emergency_contact_name || '',
      emergency_contact_phone: tenant.emergency_contact_phone || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try { await api.deleteTenant(id); fetchTenants(); }
      catch (error) { console.error('Failed:', error); }
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'text-green-600 bg-green-50';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('tenants')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}><Plus className="mr-2 h-4 w-4" /> {t('addTenant')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTenant ? t('edit') : t('add')} {t('tenants')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('fullName')}*</Label>
                    <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('email')}*</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('phone')}*</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('nationalId')}</Label>
                    <Input value={formData.national_id} onChange={(e) => setFormData({ ...formData, national_id: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t('nationality')}</Label>
                    <Input value={formData.nationality} onChange={(e) => setFormData({ ...formData, nationality: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t('emergencyContact')} Name</Label>
                    <Input value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t('emergencyContact')} {t('phone')}</Label>
                    <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} />
                  </div>
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader><CardTitle>{t('tenants')} ({tenants.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-8">Loading...</div> : tenants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No tenants yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('fullName')}</TableHead>
                    <TableHead>{t('email')}</TableHead>
                    <TableHead>{t('phone')}</TableHead>
                    <TableHead>{t('nationality')}</TableHead>
                    <TableHead>{t('riskScore')}</TableHead>
                    <TableHead>Late Payments</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell className="font-medium">{tenant.full_name}</TableCell>
                      <TableCell>{tenant.email}</TableCell>
                      <TableCell>{tenant.phone}</TableCell>
                      <TableCell>{tenant.nationality || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(tenant.risk_score)}`}>
                          {tenant.risk_score}/100
                        </span>
                      </TableCell>
                      <TableCell>
                        {tenant.late_payments > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="h-3 w-3" /> {tenant.late_payments}
                          </span>
                        )}
                        {tenant.late_payments === 0 && <span className="text-green-600">0</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(tenant)}><Edit2 className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(tenant.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ModuleIntelligenceBlock module="tenants" testId="tenants-intel" />
      </div>
    </Layout>
  );
}
